import { createRoute, useNavigate } from "@tanstack/react-router";
import { withPermission } from "../lib/with-permission";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FileText, Search, AlertCircle, Info, AlertTriangle, Bug } from "lucide-react";
import { rootRoute } from "./__root";
import { useAuth } from "../lib/use-auth";
import { useAdminLogs } from "../hooks/useAdminLogs";
import { Table } from "../components/ui/Table/Table";
import { Pagination } from "../components/ui/Pagination/Pagination";
import { Badge } from "../components/ui/Badge/Badge";
import { Input } from "../components/ui/Input/Input";
import { Select } from "../components/ui/Select/Select";
import { formatDateTime } from "../lib/date";
import styles from "./AdminLogs.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/admin/logs",
	validateSearch: (search: Record<string, unknown>) => ({
		page: Number(search.page) || 1,
		size: Number(search.size) || 20,
	}),
	component: withPermission(AdminLogsPage, "admin", "read"),
});

function AdminLogsPage() {
	const { t } = useTranslation();
	const { user } = useAuth();
	const navigate = useNavigate();
	const { page, size } = route.useSearch();
	const pageSize = size;
	const [level, setLevel] = useState("");
	const [app, setApp] = useState("");
	const [search, setSearch] = useState("");
	const [expanded, setExpanded] = useState<string | null>(null);

	useEffect(() => {
		if (user && user.role !== "admin") {
			window.location.href = "/";
		}
	}, [user]);

	const { data, isLoading } = useAdminLogs({
		level: level || undefined,
		app: app || undefined,
		search: search || undefined,
		limit: pageSize,
		offset: (page - 1) * pageSize,
	});

	const getLevelIcon = (lvl: string) => {
		switch (lvl) {
			case "error":
				return <AlertCircle size={16} />;
			case "warn":
				return <AlertTriangle size={16} />;
			case "info":
				return <Info size={16} />;
			case "debug":
				return <Bug size={16} />;
			default:
				return <FileText size={16} />;
		}
	};

	const getLevelVariant = (lvl: string) => {
		switch (lvl) {
			case "error":
				return "danger";
			case "warn":
				return "warning";
			case "info":
				return "primary";
			case "debug":
				return "muted";
			default:
				return "default";
		}
	};

	return (
		<div className={styles.container}>
			<h1 className={styles.heading}>{t("adminLogsTitle")}</h1>

			<div className={styles.filterRow}>
				<div className={styles.filterGroup}>
					<Select
						options={[
							{ value: "", label: t("allLevels") },
							{ value: "error", label: t("error") },
							{ value: "warn", label: t("warn") },
							{ value: "info", label: t("info") },
							{ value: "debug", label: t("debug") },
						]}
						value={level}
						onChange={(e) => {
							setLevel(e.target.value);
							navigate({ to: ".", search: { page: 1, size } });
						}}
					/>
					<Select
						options={[
							{ value: "", label: t("allApps") },
							{ value: "api", label: t("appApi") },
							{ value: "sap-sync", label: t("appSapSync") },
							{ value: "web", label: t("appWeb") },
						]}
						value={app}
						onChange={(e) => {
							setApp(e.target.value);
							navigate({ to: ".", search: { page: 1, size } });
						}}
					/>
				</div>
				<div className={styles.searchBox}>
					<Search size={16} className={styles.searchIcon} />
					<Input
						placeholder={t("searchLogs")}
						value={search}
						onChange={(e) => {
							setSearch(e.target.value);
							navigate({ to: ".", search: { page: 1, size } });
						}}
					/>
				</div>
			</div>

			<Table
				columns={[
					{
						key: "level",
						header: t("level"),
						render: (row) => (
							<Badge variant={getLevelVariant(row.level) as never}>
								{getLevelIcon(row.level)}
								<span style={{ marginLeft: 4 }}>{row.level}</span>
							</Badge>
						),
						width: "100px",
					},
					{
						key: "app",
						header: t("app"),
						width: "120px",
					},
					{
						key: "createdAt",
						header: t("timestamp"),
						render: (row) => formatDateTime(row.createdAt),
						width: "180px",
					},
					{
						key: "message",
						header: t("message"),
						render: (row) => (
							<div>
								<div className={styles.message}>{row.message}</div>
								{row.metadata && (
									<button
										type="button"
										className={styles.toggleMeta}
										onClick={() => setExpanded(expanded === row.id ? null : row.id)}
									>
										{expanded === row.id ? t("hideDetails") : t("showDetails")}
									</button>
								)}
								{expanded === row.id && row.metadata && (
									<pre className={styles.metadata}>{JSON.stringify(row.metadata, null, 2)}</pre>
								)}
								{expanded === row.id && row.trace && (
									<pre className={styles.trace}>{row.trace}</pre>
								)}
							</div>
						),
					},
				]}
				data={data?.items ?? []}
				isLoading={isLoading}
				emptyMessage={t("noData")}
				keyExtractor={(row) => row.id}
			/>

			{data && data.total > 0 && (
				<Pagination
					page={page}
					pageSize={pageSize}
					total={data.total}
					onPageChange={(p) => navigate({ to: ".", search: { page: p, size } })}
					onPageSizeChange={(s) => navigate({ to: ".", search: { size: s, page: 1 } })}
				/>
			)}
		</div>
	);
}
